import { useEffect } from "react";
import { useLang } from "@/context/LangContext";

const ensure = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag") return;
    if (v == null || v === "") {
      el.removeAttribute(k);
    } else {
      el.setAttribute(k, v);
    }
  });
  return el;
};

const setLink = (rel, href) => {
  if (!href) {
    document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((n) => n.remove());
    return;
  }
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Drop-in SEO updater. Reads SiteContent and applies relevant meta tags
 * to <head>. Pass a per-page override (e.g. manifesto detail) via `overrides`.
 */
export default function Seo({ content, overrides = {} }) {
  const { lang } = useLang();

  useEffect(() => {
    if (!content) return;

    const pick = (key) =>
      overrides[key] ??
      content[`${key}_${lang}`] ??
      content[`${key}_it`] ??
      content[key] ??
      "";

    const title = overrides.title || pick("seo_title");
    const description = overrides.description || pick("seo_description");
    const keywords = overrides.keywords || pick("seo_keywords");
    const robots = content.seo_robots || "index, follow";
    const author = content.seo_author || "";
    const ogImage =
      overrides.og_image ||
      content.seo_og_image ||
      content.hero_image_url ||
      "";
    const canonical =
      overrides.canonical ||
      content.seo_canonical_url ||
      (typeof window !== "undefined" ? window.location.href : "");

    if (title) document.title = title;

    ensure('meta[name="description"]', {
      tag: "meta",
      name: "description",
      content: description,
    });
    ensure('meta[name="keywords"]', {
      tag: "meta",
      name: "keywords",
      content: keywords,
    });
    ensure('meta[name="robots"]', {
      tag: "meta",
      name: "robots",
      content: robots,
    });
    ensure('meta[name="author"]', {
      tag: "meta",
      name: "author",
      content: author,
    });

    // Open Graph
    ensure('meta[property="og:title"]', {
      tag: "meta",
      property: "og:title",
      content: title,
    });
    ensure('meta[property="og:description"]', {
      tag: "meta",
      property: "og:description",
      content: description,
    });
    ensure('meta[property="og:type"]', {
      tag: "meta",
      property: "og:type",
      content: "website",
    });
    ensure('meta[property="og:locale"]', {
      tag: "meta",
      property: "og:locale",
      content: lang === "en" ? "en_GB" : "it_IT",
    });
    ensure('meta[property="og:url"]', {
      tag: "meta",
      property: "og:url",
      content: canonical,
    });
    if (ogImage) {
      ensure('meta[property="og:image"]', {
        tag: "meta",
        property: "og:image",
        content: ogImage,
      });
    }

    // Twitter
    ensure('meta[name="twitter:card"]', {
      tag: "meta",
      name: "twitter:card",
      content: ogImage ? "summary_large_image" : "summary",
    });
    ensure('meta[name="twitter:title"]', {
      tag: "meta",
      name: "twitter:title",
      content: title,
    });
    ensure('meta[name="twitter:description"]', {
      tag: "meta",
      name: "twitter:description",
      content: description,
    });
    if (ogImage) {
      ensure('meta[name="twitter:image"]', {
        tag: "meta",
        name: "twitter:image",
        content: ogImage,
      });
    }

    // Google Search Console verification
    if (content.seo_google_verification) {
      ensure('meta[name="google-site-verification"]', {
        tag: "meta",
        name: "google-site-verification",
        content: content.seo_google_verification,
      });
    }

    setLink("canonical", canonical);

    // Update <html lang>
    document.documentElement.lang = lang;
  }, [content, lang, overrides]);

  return null;
}
